import { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import EditableText from "@/components/editor/EditableText";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Booking() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [form, setForm] = useState({ name: "", email: "", phone: "", group_size: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!dateRange.from || !dateRange.to) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/booking`, {
        ...form,
        group_size: parseInt(form.group_size) || 1,
        check_in: dateRange.from.toISOString().split("T")[0],
        check_out: dateRange.to.toISOString().split("T")[0],
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Booking error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <section id="booking" data-testid="booking-section" className="wc-section bg-wc-cream" ref={ref}>
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <p className="font-accent italic text-wc-teal text-base mb-3">Book Your Stay</p>
          <h2 className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4">
            Reserve Whale Cove.
          </h2>
          <p className="font-body text-wc-slate text-base max-w-2xl mx-auto">
            Whole-property rental only. Minimum 2-night stay. One group at a time — the place is yours.
          </p>
        </motion.div>

        {submitted ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
            data-testid="booking-success-message"
          >
            <h3 className="font-display text-wc-forest text-2xl font-semibold mb-4">Request Received</h3>
            <p className="font-body text-wc-slate text-base">
              We'll check availability and get back to you shortly. Thanks for your interest in Whale Cove.
            </p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12"
          >
            {/* Calendar */}
            <div>
              <h3 className="font-display text-wc-forest text-lg font-semibold mb-4">Select Dates</h3>
              <div className="bg-white rounded-md p-4 border border-wc-driftwood/30 inline-block">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={setDateRange}
                  numberOfMonths={1}
                  disabled={{ before: new Date() }}
                  data-testid="booking-calendar"
                  classNames={{
                    day_selected: "bg-wc-teal text-wc-cream hover:bg-wc-teal hover:text-wc-cream focus:bg-wc-teal focus:text-wc-cream",
                    day_today: "bg-wc-driftwood/30 text-wc-forest",
                    day_range_middle: "aria-selected:bg-wc-teal/10 aria-selected:text-wc-forest",
                  }}
                />
              </div>
              {dateRange.from && dateRange.to && (
                <p className="font-body text-wc-teal text-sm mt-3" data-testid="selected-dates">
                  {dateRange.from.toLocaleDateString()} — {dateRange.to.toLocaleDateString()}
                </p>
              )}

              {/* Hipcamp option */}
              <div className="mt-8 p-5 bg-white/60 border border-wc-driftwood/30 rounded-md">
                <h4 className="font-display text-wc-forest text-base font-semibold mb-2">Also on Hipcamp</h4>
                <p className="font-body text-wc-slate text-sm mb-3">
                  Prefer to book through Hipcamp? You can find us there too.
                </p>
                <a
                  href="#"
                  data-testid="hipcamp-link"
                  className="font-body text-wc-teal text-sm underline hover:text-wc-forest transition-colors"
                >
                  View on Hipcamp (coming soon)
                </a>
              </div>
            </div>

            {/* Booking form */}
            <div>
              <h3 className="font-display text-wc-forest text-lg font-semibold mb-4">Your Details</h3>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
                <div>
                  <Label className="font-body text-wc-slate text-sm">Name *</Label>
                  <Input
                    required
                    value={form.name}
                    onChange={updateField("name")}
                    className="mt-1 border-wc-driftwood/40 bg-white focus:ring-wc-teal"
                    data-testid="booking-name-input"
                  />
                </div>
                <div>
                  <Label className="font-body text-wc-slate text-sm">Email *</Label>
                  <Input
                    required
                    type="email"
                    value={form.email}
                    onChange={updateField("email")}
                    className="mt-1 border-wc-driftwood/40 bg-white focus:ring-wc-teal"
                    data-testid="booking-email-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="font-body text-wc-slate text-sm">Phone</Label>
                    <Input
                      value={form.phone}
                      onChange={updateField("phone")}
                      className="mt-1 border-wc-driftwood/40 bg-white focus:ring-wc-teal"
                      data-testid="booking-phone-input"
                    />
                  </div>
                  <div>
                    <Label className="font-body text-wc-slate text-sm">Group Size *</Label>
                    <Input
                      required
                      type="number"
                      min="1"
                      value={form.group_size}
                      onChange={updateField("group_size")}
                      className="mt-1 border-wc-driftwood/40 bg-white focus:ring-wc-teal"
                      data-testid="booking-group-size-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="font-body text-wc-slate text-sm">Message / Special Requests</Label>
                  <textarea
                    value={form.message}
                    onChange={updateField("message")}
                    rows={3}
                    className="mt-1 w-full rounded-md border border-wc-driftwood/40 bg-white px-3 py-2 font-body text-sm focus:outline-none focus:ring-2 focus:ring-wc-teal"
                    data-testid="booking-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting || !dateRange.from || !dateRange.to}
                  className="w-full bg-wc-forest text-wc-cream hover:bg-wc-teal font-body tracking-wide"
                  data-testid="booking-submit-button"
                >
                  {submitting ? "Sending..." : "Request Booking"}
                </Button>
                <p className="font-body text-wc-slate/60 text-xs text-center">
                  Nightly rate: TBD. We'll confirm pricing and availability via email.
                </p>
              </form>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
